import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';

import { AnalyticsEventsService } from 'src/app/components/logging/analytics-events.service';
import { AnalyticsService } from 'src/app/components/logging/analytics.service';
import { environment } from 'src/environments/environment';

const EDITABLE_INPUT_TYPES = new Set([
  'TEXT',
  'PASSWORD',
  'FILE',
  'SEARCH',
  'EMAIL',
  'NUMBER',
  'DATE',
  'TEL',
  'URL',
]);

/**
 * Owns shared shell startup for the common header.
 */
@Injectable({
  providedIn: 'root',
})
export class CommonHeaderBootstrapService {
  private readonly document = inject(DOCUMENT);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly analyticsEventsService = inject(AnalyticsEventsService);

  private initialized = false;
  private readonly keydownHandler = (event: KeyboardEvent) => {
    this.handleBackspaceKeydown(event);
  };

  initialize(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    this.analyticsEventsService.initialize();
    this.analyticsService.load(
      environment.TAG_MANAGER_CONTAINER_ID ?? undefined,
      environment.TAG_MANAGER_AUTH ?? undefined,
      environment.TAG_MANAGER_ENV ?? undefined,
    );
    this.document.addEventListener('keydown', this.keydownHandler);
  }

  destroy(): void {
    if (!this.initialized) {
      return;
    }

    this.document.removeEventListener('keydown', this.keydownHandler);
    this.initialized = false;
  }

  private handleBackspaceKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Backspace' && event.keyCode !== 8) {
      return;
    }

    const target = (event.target ||
      (event as KeyboardEvent & { srcElement?: EventTarget })
        .srcElement) as HTMLElement | null;

    if (!target) {
      event.preventDefault();
      return;
    }

    const tagName = target.tagName?.toUpperCase();
    let doPrevent = true;

    if (tagName === 'INPUT') {
      const inputType = ((target as HTMLInputElement).type || '').toUpperCase();
      if (EDITABLE_INPUT_TYPES.has(inputType)) {
        doPrevent =
          (target as HTMLInputElement).readOnly ||
          (target as HTMLInputElement).disabled;
      }
    } else if (tagName === 'TEXTAREA') {
      doPrevent =
        (target as HTMLTextAreaElement).readOnly ||
        (target as HTMLTextAreaElement).disabled;
    }

    if (doPrevent) {
      event.preventDefault();
    }
  }
}
